import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
} from 'axios';

import { ApiError } from './ApiError';
import {
  AuthTokenCancelledError,
  getAccessToken,
  handleUnauthorized,
  refreshToken,
} from './authToken';
import { CacheItem, ServerErrorResponse } from './types';

type GetCachedOptions = {
  logoutOnInternalServerError?: boolean;
};

class BaseApiService {
  protected api: AxiosInstance;
  private cache: Map<string, CacheItem<unknown>> = new Map();

  constructor() {
    this.api = axios.create({
      // In dev, use the Vite proxy (/api -> backend) to bypass CORS restrictions.
      // In prod the calls go straight to the configured backend base URL.
      baseURL: import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupErrorHandling();
  }

  private setupErrorHandling() {
    this.api.interceptors.response.use(
      response => response,
      (error: AxiosError<ServerErrorResponse<unknown>>): Promise<ApiError<unknown>> => {
        if (error instanceof AuthTokenCancelledError) {
          return Promise.reject(ApiError.fromError(error));
        }

        let apiError: ApiError<unknown>;
        if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message)) {
          apiError = ApiError.timeout;
        } else if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          apiError = ApiError.fromResponse(error.status, error.response.data);
        } else if (error.request) {
          // The request was made but no response was received
          apiError = ApiError.noResponse;
        } else {
          // Something happened in setting up the request that triggered an Error
          apiError = ApiError.fromMessage(error.message);
        }

        return Promise.reject(apiError);
      }
    );
  }

  protected async getCached<T>(
    cacheKey: string,
    url: string,
    cacheTime: number = 60000, // Default cache time: 1 minute
    config?: AxiosRequestConfig,
    options?: GetCachedOptions
  ): Promise<T> {
    const cachedItem = this.cache.get(cacheKey);

    if (cachedItem && cachedItem.expiry > Date.now()) {
      return cachedItem.data as T;
    }

    const response: AxiosResponse<T> = await this.api.get(url, config).catch(async error => {
      if (
        options?.logoutOnInternalServerError &&
        error instanceof ApiError &&
        error.status === 500
      ) {
        await handleUnauthorized().catch(console.debug);
      }

      throw error;
    });

    this.cache.set(cacheKey, {
      data: response.data,
      expiry: Date.now() + cacheTime,
    });

    return response.data;
  }

  protected clearCache(): void {
    this.cache.clear();
  }

  protected deleteCache(key: string): void {
    this.cache.delete(key);
  }

  protected request<T>(
    method: string,
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): {
    promise: Promise<T>;
    cancel: () => void;
  } {
    const source: CancelTokenSource = axios.CancelToken.source();

    const promise = this.api
      .request<T>({
        method,
        url,
        data,
        ...config,
        cancelToken: source.token,
      })
      .then(response => response.data);

    return {
      promise,
      cancel: () => source.cancel('Request cancelled by user'),
    };
  }

  protected get<T>(url: string, config?: AxiosRequestConfig) {
    return this.request<T>('GET', url, undefined, config);
  }

  protected post<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return this.request<T>('POST', url, data, config);
  }

  protected put<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return this.request<T>('PUT', url, data, config);
  }

  protected patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return this.request<T>('PATCH', url, data, config);
  }

  protected delete<T>(url: string, config?: AxiosRequestConfig) {
    return this.request<T>('DELETE', url, undefined, config);
  }
}

/**
 * Authenticated variant: attaches a Bearer token (from the configured auth token
 * provider) to every request and retries once on 401 after a token refresh.
 * The token provider is a no-op until auth is wired in a later task.
 */
export class AuthenticatedApiService extends BaseApiService {
  constructor() {
    super();
    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.api.interceptors.request.use(
      async config => {
        const token = await getAccessToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        return config;
      },
      error => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      response => response,
      async error => {
        if (error.response && error.response.status === 401) {
          await refreshToken();

          return this.api.request(error.config);
        }

        return Promise.reject(error);
      }
    );
  }
}

export class AnonymousApiService extends BaseApiService {}

/**
 * Lead-path variant (Task #211): non-payment lead endpoints are served by OUR
 * OWN backend (the Node server in ./server), never Azure, in every
 * environment. The base is the relative path '/lead-api' everywhere: in dev
 * the Vite proxy forwards it to the local API, and in production the same
 * Node server serves the built frontend and mounts the API under '/lead-api'
 * on the same origin — so no VITE_ env var is involved and the lead path can
 * never silently point back at Azure.
 */
export class LeadBackendApiService extends BaseApiService {
  constructor() {
    super();
    this.api.defaults.baseURL = '/lead-api';
    // Lead Desk provider calls can be slow, but must never leave ops waiting
    // indefinitely. Applies to login, listings, mutations and detail loads.
    this.api.defaults.timeout = 15_000;
  }
}
